import * as React from 'react';
import { isUserPickerSupportEnabled } from '@helpcenter/feature-flags';
import type { FieldFormProps } from '@helpcenter/field-types';
import type { FieldOption } from '@helpcenter/form-select';
import { FormAsyncCreatableSingleSelect, FormAsyncSingleSelect } from '@helpcenter/form-select';
import type { WrappedComponentProps } from 'react-intl-next';
import { injectIntl } from 'react-intl-next';
import { UserPickerItem } from 'view/common/user-picker-item';
import { components } from '@atlaskit/select';
import type { ValueType, SelectComponentsConfig, OptionType } from '@atlaskit/select';
import { toSingleSelectValue } from '@atlassian/help-center-common-util/select';
import messages from '../messages';
import { isEmail } from '../user-picker-helper';

export interface User {
    isNew?: boolean;
    value: string;
    label: string;
    iconUrl?: string;
    email: string;
    accountId: string;
}

export type CreateLabelType = 'add' | 'create' | undefined;

export type UserPickerOption = FieldOption<string>;
export interface Props extends FieldFormProps<string> {
    label: string;
    defaultUser?: User;
    canCreateUser: boolean;
    loadOptions: (inputValue: string, callback: (options: UserPickerOption[]) => void) => void;
    onMenuOpen?: () => void;
    isLoading?: boolean;
    doInitialSearch?: boolean; // Whether we load options by default
    createLabelType?: CreateLabelType;
    defaultOptions?: OptionType[];
    valueOverride?: User;
}

export class UserPickerDumb extends React.PureComponent<Props & WrappedComponentProps> {
    onChangeForSelectField = (value: ValueType<UserPickerOption>) => {
        const selectedValue = toSingleSelectValue(value);
        if (selectedValue == null) {
            isUserPickerSupportEnabled()
                ? this.props.onChange?.(this.props.id, '', false, {
                      rawObject: value ?? {},
                      rawValue: { accountId: '' },
                  })
                : this.props.onChange?.(this.props.id, '', false, {
                      rawObject: value ?? {},
                  });
            return;
        }
        isUserPickerSupportEnabled()
            ? this.props.onChange?.(this.props.id, selectedValue.value, false, {
                  rawObject: value ?? {},
                  rawValue: selectedValue ? { accountId: selectedValue.value } : null,
              })
            : this.props.onChange?.(this.props.id, selectedValue.value, false, {
                  rawObject: value ?? {},
              });
    };

    userPickerItem: SelectComponentsConfig<UserPickerOption>['SingleValue'] = ({ data }) => {
        const { iconUrl, label, email, isNew } = data as UserPickerOption;
        // Suppressing existing violation. Please fix this.
        // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment
        return <UserPickerItem name={label} icon={iconUrl} email={email} isNew={isNew} />;
    };

    userPickerOption: SelectComponentsConfig<UserPickerOption>['Option'] = ({ data, ...props }) => {
        const { iconUrl, label, email, isNew } = data as UserPickerOption;
        return (
            <components.Option {...props} data={data}>
                {/* Suppressing existing violation. Please fix this. */}
                {/* eslint-disable-next-line @typescript-eslint/no-unsafe-assignment */}
                <UserPickerItem name={label} icon={iconUrl} email={email} isNew={isNew} />
            </components.Option>
        );
    };

    formatCreateLabel = (input: string) => {
        const { intl, createLabelType } = this.props;
        if (createLabelType === 'create') {
            return intl.formatMessage(messages.createAction, { value: input });
        }
        if (createLabelType === 'add') {
            return intl.formatMessage(messages.addAction, { value: input });
        }
        return input;
    };

    isValidNewOption = (inputValue: string, _: string, selectOptions: UserPickerOption[]) => {
        // Check whether the input value already corresponds to one of the available option.
        // We check whether the email matches the input exactly otherwise we show the option to create
        const isInputValueAnOption =
            selectOptions &&
            Array.isArray(selectOptions) &&
            selectOptions.filter((option) => option.email === inputValue).length > 0;

        return !isInputValueAnOption && isEmail(inputValue);
    };

    shouldShowCreatabelSelect = (): boolean => {
        const { canCreateUser } = this.props;
        if (canCreateUser) {
            return true;
        }
        return false;
    };

    getNoOptionsCreatable = () => {
        const { intl } = this.props;
        return intl.formatMessage(messages.noOptionsCreatable);
    };

    getNoOptions = () => {
        const { intl } = this.props;
        return intl.formatMessage(messages.noOptions);
    };

    render() {
        const {
            disabled,
            defaultUser,
            doInitialSearch,
            canCreateUser,
            defaultOptions,
            createLabelType,
            onMenuOpen,
            isLoading,
            intl,
            onChange,
            ...otherProps
        } = this.props;

        const customComponents = {
            SingleValue: this.userPickerItem,
            Option: this.userPickerOption,
        };

        if (this.shouldShowCreatabelSelect()) {
            const useShowNewLabelForCreatedValues = createLabelType === 'create';

            return (
                <FormAsyncCreatableSingleSelect
                    {...otherProps}
                    formatCreateLabel={this.formatCreateLabel}
                    isDisabled={disabled}
                    isValidNewOption={this.isValidNewOption}
                    components={customComponents}
                    onChange={this.onChangeForSelectField}
                    onMenuOpen={onMenuOpen}
                    isLoading={isLoading}
                    defaultValue={defaultUser}
                    defaultOptions={doInitialSearch || defaultOptions}
                    placeholder={intl.formatMessage(messages.placeholder)}
                    noOptionsMessage={this.getNoOptionsCreatable}
                    showNewLabelForCreatedValues={useShowNewLabelForCreatedValues}
                />
            );
        }

        return (
            <FormAsyncSingleSelect
                {...otherProps}
                loadOptions={this.props.loadOptions}
                isDisabled={disabled}
                components={customComponents}
                onMenuOpen={onMenuOpen}
                isLoading={isLoading}
                onChange={this.onChangeForSelectField}
                defaultValue={defaultUser}
                placeholder={intl.formatMessage(messages.placeholder)}
                defaultOptions={doInitialSearch || defaultOptions}
                noOptionsMessage={this.getNoOptions}
            />
        );
    }
}

export default injectIntl(UserPickerDumb);
